/**
 * Таблица спецификации: обработчики, загрузка, DnD-сортировка, количество,
 * выделение строк, комментарии, свёртка комнат.
 *
 * Объединено в один модуль, потому что все эти фичи работают на одной таблице
 * и делят общий state (state.selectedProducts, state.sortableInstances).
 */

import { apiRequest } from '../../api.js';
import { state } from './store.js';
import { applyColumnVisibilitySettings } from './columns.js';
import { updateActionButtons } from './actions.js';
import { openDeleteProductModal } from './modals.js';
import { openSpecChat } from '../spec-chat/index.js';

// ─── Загрузка таблицы ──────────────────────────────────────────────────────
export async function loadSpecTable() {
    const container = document.getElementById('spec-table-container');
    if (!container) return;

    const formData = new FormData();
    state.selectedFilters.status.forEach(id => formData.append('status[]', id));
    state.selectedFilters.manufacturer.forEach(id => formData.append('manufacturer[]', id));

    try {
        // Бесшовное обновление: старая таблица остаётся на экране, пока грузятся данные —
        // никакого спиннера и схлопывания высоты, скролл не прыгает. Подменяем только по факту ответа.
        // Сервер отдаёт HTML, apiRequest вернёт строку (Content-Type: text/html).
        const html = await apiRequest(`/project/${state.projectId}/spec/table`, {
            method: 'POST',
            body: formData,
        });
        container.innerHTML = html;

        initSortable();
        updateButtonStates();
        applyColumnVisibilitySettings();
        updateActionButtons();

        // Кнопки Add Product переинициализируются через делегированный обработчик ниже —
        // достаточно что initTableHandlers() уже установил document-level слушатель.
        if (typeof window.bindAddProductButtons === 'function') {
            window.bindAddProductButtons();
        }
    } catch (e) {
        console.error('Error loading spec table:', e);
        container.innerHTML = '<div class="alert alert-danger">Ошибка загрузки данных</div>';
    }
}

// ─── Drag & Drop ──────────────────────────────────────────────────────────
function initSortable() {
    state.sortableInstances.forEach(i => i.destroy());
    state.sortableInstances = [];

    if (typeof Sortable === 'undefined') return;

    document.querySelectorAll('.rooms-list').forEach(roomContainer => {
        const roomId = roomContainer.dataset.room;
        const sortable = new Sortable(roomContainer, {
            handle: '.adm_spec_item_table_td_drag, .move-item',
            draggable: '.row-item',
            animation: 150,
            ghostClass: 'sortable-ghost',
            dragClass: 'sortable-drag',
            onEnd(evt) {
                updateProductOrder(roomId, evt.oldIndex, evt.newIndex);
            },
        });
        state.sortableInstances.push(sortable);
    });
}

async function updateProductOrder(roomId, oldIndex, newIndex) {
    if (oldIndex === newIndex) return;

    const products = [];
    document.querySelectorAll(`.rooms-list[data-room="${roomId}"] .row-item`).forEach((item, index) => {
        products.push({ id: item.dataset.id, sorting: index + 1 });
    });

    try {
        await apiRequest(`/project/${state.projectId}/spec/reorder`, {
            method: 'POST',
            body: { room_id: roomId, products },
        });
    } catch (e) {
        console.error('Error updating product order:', e);
    }
}

// ─── Количество ───────────────────────────────────────────────────────────
async function changeQuantity(productId, type) {
    const qtyElement = document.getElementById(`product_qty_${productId}`);
    if (!qtyElement) return;

    let qty = parseInt(qtyElement.textContent) || 1;
    if (type === 'plus') qty++;
    else if (type === 'minus' && qty > 1) qty--;
    qtyElement.textContent = qty;

    try {
        const data = await apiRequest(`/project/${state.projectId}/spec/update-quantity`, {
            method: 'POST',
            body: { product_id: productId, quantity: qty },
        });
        if (data && data.success && data.totals) {
            updateTotals(data.totals);
        }
    } catch (e) {
        console.error('Error updating quantity:', e);
    }
}

function updateTotals(totals) {
    const totalElement = document.querySelector('.adm_spec_totals_price');
    if (totalElement && totals.total) {
        totalElement.textContent = `€ ${totals.total}`;
    }
}

// ─── Чат-комментарии (desktop + mobile) ───────────────────────────────────
// DEV-329: popup облачка теперь — тред сообщений (см. features/spec-chat).
// При открытии popup’а подгружаем сообщения и отмечаем прочитанным.

function toggleCommentDesktop(handler) {
    const container = handler.closest('[data-spec_comment_container]');
    if (!container) return;

    const currentId = container.dataset.spec_comment_container;
    const isOpening = !container.classList.contains('active');

    container.classList.toggle('active');

    document.querySelectorAll('[data-spec_comment_container]').forEach(item => {
        if (item.dataset.spec_comment_container !== currentId) {
            item.classList.remove('active');
        }
    });

    if (isOpening) openSpecChat(currentId);
}

function toggleCommentMobile(handler) {
    const id = handler.dataset.mobile_comment_handler_id;
    const isOpening = !handler.parentElement.classList.contains('active');

    handler.parentElement.classList.toggle('active');

    const commentBlock = document.querySelector(`[data-mobile_comment_block_id="${id}"]`);
    if (commentBlock) commentBlock.classList.toggle('active');

    if (isOpening) openSpecChat(id);
}

// ─── Свёртка комнат ───────────────────────────────────────────────────────
async function toggleRoomCollapse(roomId) {
    const container = document.getElementById(`room_container_${roomId}`);
    if (!container) return;

    container.classList.toggle('is-collapsed');

    try {
        await apiRequest(`/project/${state.projectId}/room/toggle-collapse`, {
            method: 'POST',
            body: {
                room_id: roomId,
                collapsed: container.classList.contains('is-collapsed'),
            },
        });
    } catch (e) {
        console.error('Error toggling room collapse:', e);
    }
}

// ─── Выделение строк ──────────────────────────────────────────────────────
function updateProductSelection(checkbox) {
    const productId = checkbox.closest('[data-id]').dataset.id;
    if (checkbox.checked) {
        if (!state.selectedProducts.includes(productId)) state.selectedProducts.push(productId);
    } else {
        const index = state.selectedProducts.indexOf(productId);
        if (index > -1) state.selectedProducts.splice(index, 1);
    }
}

export function updateButtonStates() {
    const hasSelection = state.selectedProducts.length > 0;

    document.querySelectorAll('.adm_spec_button_export').forEach(btn => {
        btn.disabled = !hasSelection;
    });

    let hasNotCalculated = false;
    let hasCalculated = false;

    state.selectedProducts.forEach(productId => {
        const statusInput = document.querySelector(`input[name="status_${productId}"]`);
        if (statusInput) {
            const statusId = parseInt(statusInput.value);
            if (statusId === 1) hasNotCalculated = true;
            if (statusId === 3) hasCalculated = true;
        }
    });

    const estimationBtn = document.querySelector('.button_x:nth-child(3)');
    if (estimationBtn) estimationBtn.disabled = !hasNotCalculated;

    const orderBtn = document.querySelector('.button_x:nth-child(4)');
    if (orderBtn) orderBtn.disabled = !hasCalculated;
}

// ─── Инициализация всех обработчиков таблицы ──────────────────────────────
export function initTableHandlers() {
    // Все обработчики делегированные (для работы с динамически подгружаемой таблицей)
    document.addEventListener('click', e => {
        // Удаление товара (desktop+mobile)
        const removeBtn = e.target.closest('.adm_spec_tool_remove, .delete-product-spec, .adm_spec_icon_remove_mobile');
        if (removeBtn) {
            e.preventDefault();
            if (removeBtn.classList.contains('disabled')) return;
            const productId = removeBtn.dataset.id;
            if (productId) {
                state.deleteProductId = productId;
                openDeleteProductModal(productId);
            }
            return;
        }

        // Комментарий desktop
        const commentHandler = e.target.closest('[data-spec_comment_handler]');
        if (commentHandler) {
            e.stopPropagation();
            toggleCommentDesktop(commentHandler);
            return;
        }

        // Комментарий mobile
        const mobileCommentHandler = e.target.closest('[data-mobile_comment_handler_id]');
        if (mobileCommentHandler) {
            toggleCommentMobile(mobileCommentHandler);
            return;
        }

        // Количество
        const qtyBtn = e.target.closest('.change-qty');
        if (qtyBtn) {
            e.preventDefault();
            changeQuantity(qtyBtn.dataset.id, qtyBtn.dataset.type);
            return;
        }

        // Свёртка комнат
        const collapseBtn = e.target.closest('.btn_collapse_room');
        if (collapseBtn) {
            toggleRoomCollapse(collapseBtn.dataset.id);
            return;
        }

        // Клик вне комментария — закрыть все открытые
        if (!e.target.closest('[data-spec_comment_container]')) {
            document.querySelectorAll('[data-spec_comment_container].active').forEach(el => {
                el.classList.remove('active');
            });
        }
    });

    document.addEventListener('change', e => {
        // Выделение одного товара. (select-all обрабатывается в actions.js,
        // он эмитит change на каждом .checkbox-select-product — этот хендлер
        // отрабатывает на каждом и синхронизирует state.selectedProducts.)
        if (e.target.matches('.checkbox-select-product')) {
            updateProductSelection(e.target);
            updateButtonStates();
        }
    });
}
